import { objectToQueryParams } from './utils';
import type { 
  TimeSeriesMeta, 
  TimeSeriesData, 
  TimeSeriesDataPoint, 
  ForecastData, 
  AIAnalysisData,
  PaginatedResponse,
  OpenAIModel
} from './types';
import { apiLogger, LogLevel } from './api-logger';

/**
 * Base API handling function to standardize error responses
 */
async function handleApiRequest<T>(url: string, options?: RequestInit): Promise<T> {
  const method = options?.method || 'GET';
  const requestId = generateRequestId();
  
  try {
    // Extract and sanitize request body for logging
    let requestBody: any = undefined;
    if (options?.body) {
      if (options.body instanceof FormData) {
        // For FormData, create a summary of keys
        const formDataKeys: string[] = [];
        options.body.forEach((_, key) => {
          formDataKeys.push(key);
        });
        requestBody = { type: 'FormData', keys: formDataKeys };
      } else if (typeof options.body === 'string') {
        try {
          // Try to parse as JSON
          requestBody = JSON.parse(options.body);
        } catch {
          // If not JSON, just log that it's string data
          requestBody = { type: 'string', length: options.body.length };
        }
      } else {
        // For other types, just log the type
        requestBody = { type: typeof options.body };
      }
    }
    
    // Prepare headers summary for logging
    let headersSummary: Record<string, string> = {};
    if (options?.headers) {
      if (options.headers instanceof Headers) {
        options.headers.forEach((value, key) => {
          // Exclude authorization headers from logs
          if (!key.toLowerCase().includes('authorization')) {
            headersSummary[key] = value;
          }
        });
      } else if (typeof options.headers === 'object') {
        headersSummary = { ...options.headers as Record<string, string> };
        // Remove sensitive headers
        delete headersSummary['authorization'];
        delete headersSummary['Authorization'];
      }
    }
    
    // Log the request start with enhanced details
    apiLogger.log(LogLevel.INFO, `API Request: ${method} ${url}`, { 
      requestId,
      method,
      url,
      headers: headersSummary,
      body: requestBody,
      timestamp: new Date().toISOString() 
    });
    
    // For tests to pass correctly, we need to handle the case where options is undefined
    // separately from the case where it's provided
    const startTime = Date.now();
    const response = options ? await fetch(url, options) : await fetch(url);
    const duration = Date.now() - startTime;
    
    // Get response headers if available
    const responseHeaders: Record<string, string> = {};
    
    // In test environment, headers might not be available or might be different
    if (response.headers && typeof response.headers.forEach === 'function') {
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value;
      });
    }
    
    // Get content length if available
    let contentLength = response.headers && typeof response.headers.get === 'function' 
      ? response.headers.get('content-length') 
      : null;
    
    // Parse the JSON response
    let data: any;
    let responseText: string = '';
    let payloadSize: number = 0;
    
    try {
      // In test environment, Response might not be clonable, so we need to handle this case
      let responseTextPromise: Promise<string>;
      let responseJsonPromise: Promise<any>;
      
      // Check if response is clonable (has clone method)
      if (typeof response.clone === 'function') {
        // Clone the response before consuming it to get payload size
        const responseClone = response.clone();
        // Get response text for size tracking
        responseTextPromise = responseClone.text();
        // Use original response for JSON
        responseJsonPromise = response.json();
      } else {
        // In test environment, just use json directly
        responseJsonPromise = response.json();
        // We won't have size information
        responseTextPromise = Promise.resolve('');
      }
      
      // Wait for both promises to resolve
      const [responseJson, textContent] = await Promise.all([responseJsonPromise, responseTextPromise]);
      data = responseJson;
      responseText = textContent;
      payloadSize = responseText.length;
      
      // If we didn't get text content but have content-length header, use that
      if (payloadSize === 0 && contentLength) {
        payloadSize = parseInt(contentLength, 10);
      }
    } catch (error) {
      // If JSON parsing fails
      if (!response.ok) {
        const errorMessage = `API error: ${response.status} ${response.statusText}`;
        apiLogger.log(LogLevel.ERROR, errorMessage, {
          requestId,
          method,
          url,
          status: response.status,
          statusText: response.statusText,
          responseSize: payloadSize,
          responseHeaders,
          duration,
          error
        });
        throw new Error(errorMessage);
      }
      throw error;
    }
    
    // If response is not OK, throw an error with detailed logging
    if (!response.ok) {
      const errorDetail = data?.detail || `API error: ${response.status} ${response.statusText}`;
      
      // Log the failed request details
      apiLogger.log(LogLevel.ERROR, `Failed request: ${method} ${url}`, {
        requestId,
        method,
        url,
        status: response.status,
        statusText: response.statusText,
        responseSize: payloadSize,
        responseHeaders,
        error: errorDetail,
        responseData: data,
        duration,
        timestamp: new Date().toISOString()
      });
      
      const error = new Error(typeof errorDetail === 'object' ? JSON.stringify(errorDetail) : errorDetail);
      // Add the response data as a property to the error object for debugging
      (error as any).responseData = data;
      throw error;
    }
    
    // Calculate data size for logging
    interface DataSummary {
      type: string;
      size: number;
      count?: number;
    }
    
    let dataSummary: DataSummary = {
      type: typeof data,
      size: payloadSize
    };
    
    // Add array length if it's an array
    if (Array.isArray(data)) {
      dataSummary.count = data.length;
    }
    
    // Log successful response
    apiLogger.log(LogLevel.INFO, `API Success: ${method} ${url}`, {
      requestId,
      method,
      url,
      status: response.status,
      responseSize: payloadSize,
      contentType: responseHeaders['content-type'],
      dataSummary,
      duration,
      timestamp: new Date().toISOString()
    });
    
    return data as T;
  } catch (error) {
    // Log any uncaught errors that occurred during request processing
    apiLogger.log(LogLevel.ERROR, `API Request failed: ${method} ${url}`, {
      requestId,
      method,
      url,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorType: error instanceof Error ? error.constructor.name : typeof error,
      stack: error instanceof Error ? error.stack : undefined,
      // Include information about network status if available
      networkStatus: navigator.onLine ? 'online' : 'offline',
      timestamp: new Date().toISOString()
    });
    
    // Format and rethrow the error to prevent [object Object] in UI
    if (error instanceof Error) {
      // Add response data to error if it doesn't have it
      if (typeof (error as any).responseData === 'undefined') {
        (error as any).responseData = { errorType: error.constructor.name, message: error.message };
      }
      throw error;
    } else if (typeof error === 'object' && error !== null) {
      // Handle object errors by converting to a string
      try {
        // Create proper Error object with formatted message
        const errorMsg = JSON.stringify(error);
        const formattedError = new Error(errorMsg);
        // Add the original object as responseData for debugging
        (formattedError as any).responseData = error;
        throw formattedError;
      } catch (jsonError) {
        // If JSON stringify fails, create a generic error with the object as responseData
        const genericError = new Error(`API error: ${method} ${url}`);
        try {
          (genericError as any).responseData = { ...error };
        } catch {
          // If spreading fails, just use a simple object
          (genericError as any).responseData = { errorType: typeof error, originalError: error };
        }
        throw genericError;
      }
    } else {
      // For primitive types like string, number, etc.
      const simpleError = new Error(String(error) || 'Unknown API error');
      (simpleError as any).responseData = { errorType: typeof error, originalError: String(error) };
      throw simpleError;
    }
  }
}

/**
 * Generate a unique ID for request tracking
 */
function generateRequestId(): string {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
}

/**
 * Time Series API functions
 */
export const timeSeriesApi = {
  /**
   * Get all time series with pagination
   */
  async getAll(skip: number = 0, limit: number = 100): Promise<PaginatedResponse<TimeSeriesMeta>> {
    let url = `/api/timeseries/?skip=${skip}&limit=${limit}`;
    return handleApiRequest<PaginatedResponse<TimeSeriesMeta>>(url);
  },
  
  /**
   * Get a time series by ID
   */
  async getById(id: number | string, includeData: boolean = false): Promise<TimeSeriesData> {
    return handleApiRequest<TimeSeriesData>(
      `/api/timeseries/${id}?include_data=${includeData}`
    );
  },
  
  /**
   * Create a new time series
   */
  async create(data: Partial<TimeSeriesData>): Promise<TimeSeriesData> {
    const params = objectToQueryParams(data);
    return handleApiRequest<TimeSeriesData>(
      `/api/timeseries/?${params}`,
      { method: 'POST' }
    );
  },
  
  /**
   * Update a time series
   */
  async update(id: number | string, data: Partial<TimeSeriesData>): Promise<TimeSeriesData> {
    const params = objectToQueryParams(data);
    return handleApiRequest<TimeSeriesData>(
      `/api/timeseries/${id}?${params}`,
      { method: 'PUT' }
    );
  },
  
  /**
   * Delete a time series
   */
  async delete(id: number | string): Promise<{ message: string }> {
    return handleApiRequest<{ message: string }>(
      `/api/timeseries/${id}`,
      { method: 'DELETE' }
    );
  },
  
  /**
   * Add data points to a time series
   */
  async addDataPoints(id: number | string, dataPoints: TimeSeriesDataPoint[]): Promise<{ message: string, datapoints: TimeSeriesDataPoint[] }> {
    return handleApiRequest<{ message: string, datapoints: TimeSeriesDataPoint[] }>(
      `/api/timeseries/${id}/data`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataPoints)
      }
    );
  },
  
  /**
   * Upload CSV file to a time series
   */
  async uploadCsv(
    id: number | string,
    file: File,
    timestampCol: string = 'timestamp',
    valueCol: string = 'value',
    interpolationMethod: string = 'linear'
  ): Promise<{ message: string }> {
    const formData = new FormData();
    formData.append('file', file);

    return handleApiRequest<{ message: string }>(
      `/api/timeseries/${id}/upload-csv?timestamp_col=${timestampCol}&value_col=${valueCol}&interpolation_method=${interpolationMethod}`,
      {
        method: 'POST',
        body: formData
      }
    );
  }
};

/**
 * Forecast API functions
 */

export const forecastApi = {
  /**
   * Get all forecasts for a time series
   */
  async getAll(timeSeriesId: number | string): Promise<{ forecasts: ForecastData[] }> {
    return handleApiRequest<{ forecasts: ForecastData[] }>(
      `/api/forecast/${timeSeriesId}`
    );
  },
  
  /**
   * Get a forecast by ID
   */
  async getById(timeSeriesId: number | string, forecastId: number | string, includePoints: boolean = false): Promise<ForecastData> {
    return handleApiRequest<ForecastData>(
      `/api/forecast/${timeSeriesId}/${forecastId}?include_points=${includePoints}`
    );
  },
  
  /**
   * Create a new forecast
   */
  async create(timeSeriesId: number | string, params: Record<string, any>): Promise<ForecastData> {
    const queryParams = objectToQueryParams(params);
    return handleApiRequest<ForecastData>(
      `/api/forecast/${timeSeriesId}?${queryParams}`,
      { method: 'POST' }
    );
  },
  
  /**
   * Delete a forecast
   */
  async delete(timeSeriesId: number | string, forecastId: number | string): Promise<{ message: string }> {
    return handleApiRequest<{ message: string }>(
      `/api/forecast/${timeSeriesId}/${forecastId}`,
      { method: 'DELETE' }
    );
  }
};

/**
 * AI API functions
 */
export const aiApi = {
  /**
   * Perform AI analysis on a time series
   */
  async analyze(timeSeriesId: number | string, analysisType: string, parameters?: Record<string, any>): Promise<AIAnalysisData> {
    const queryParams = new URLSearchParams({
      analysis_type: analysisType
    });
    
    if (parameters) {
      queryParams.append('parameters', JSON.stringify(parameters));
    }
    
    return handleApiRequest<AIAnalysisData>(
      `/api/ai/${timeSeriesId}/analyze?${queryParams.toString()}`,
      { method: 'POST' }
    );
  },
  
  /**
   * Get all AI analyses for a time series
   */
  async getAll(timeSeriesId: number | string): Promise<{ analyses: AIAnalysisData[] }> {
    return handleApiRequest<{ analyses: AIAnalysisData[] }>(
      `/api/ai/${timeSeriesId}/analyses`
    );
  },

  /**
   * Analyze CSV file to detect columns
   */
  async analyzeCsvColumns(file: File): Promise<{ timestamp_column: string, value_column: string }> {
    const formData = new FormData();
    formData.append('file', file);

    return handleApiRequest<{ timestamp_column: string, value_column: string }>(
      '/api/ai/analyze_csv',
      {
        method: 'POST',
        body: formData
      }
    );
  },

  /**
   * Recognize columns in CSV file using OpenAI
   */
  async recognizeColumns(file: File): Promise<{ timestamp_column: string, value_column: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await handleApiRequest<{
      status: string,
      filename: string,
      timestamp_column: string,
      value_column: string
    }>(
      '/api/ai/recognize_columns',
      {
        method: 'POST',
        body: formData
      }
    );

    return {
      timestamp_column: response.timestamp_column,
      value_column: response.value_column
    };
  }
};

/**
 * Visualization API functions
 */
export const visualizationApi = {
  /**
   * Get plot for a time series
   */
  async getPlot(
    timeseriesId: number | string, 
    options: { 
      width?: number; 
      height?: number; 
      plotType?: string; 
      includeTrend?: boolean; 
      includeStats?: boolean;
      startDate?: string;
      endDate?: string;
    } = {}
  ): Promise<{ image: string }> {
    const params = new URLSearchParams();
    
    if (options.width) params.append('width', options.width.toString());
    if (options.height) params.append('height', options.height.toString());
    if (options.plotType) params.append('plot_type', options.plotType);
    if (options.includeTrend !== undefined) params.append('include_trend', options.includeTrend.toString());
    if (options.includeStats !== undefined) params.append('include_stats', options.includeStats.toString());
    if (options.startDate) params.append('start_date', options.startDate);
    if (options.endDate) params.append('end_date', options.endDate);
    
    return handleApiRequest<{ image: string }>(
      `/api/visualization/${timeseriesId}/plot?${params.toString()}`
    );
  },
  
  /**
   * Get histogram for a time series
   */
  async getHistogram(
    timeseriesId: number | string, 
    options: { 
      width?: number; 
      height?: number; 
      bins?: number;
      includeStats?: boolean;
    } = {}
  ): Promise<{ image: string }> {
    const params = new URLSearchParams();
    
    if (options.width) params.append('width', options.width.toString());
    if (options.height) params.append('height', options.height.toString());
    if (options.bins) params.append('bins', options.bins.toString());
    if (options.includeStats !== undefined) params.append('include_stats', options.includeStats.toString());
    
    return handleApiRequest<{ image: string }>(
      `/api/visualization/${timeseriesId}/histogram?${params.toString()}`
    );
  },
  
  /**
   * Compare multiple time series
   */
  async compareTimeseries(
    ids: (number | string)[], 
    options: { 
      width?: number; 
      height?: number; 
      normalize?: boolean;
    } = {}
  ): Promise<{ image: string; series_names: string[] }> {
    const params = new URLSearchParams();
    
    ids.forEach(id => params.append('ids', id.toString()));
    
    if (options.width) params.append('width', options.width.toString());
    if (options.height) params.append('height', options.height.toString());
    if (options.normalize !== undefined) params.append('normalize', options.normalize.toString());
    
    return handleApiRequest<{ image: string; series_names: string[] }>(
      `/api/visualization/compare?${params.toString()}`
    );
  }
};

/**
 * OpenAI Models API functions
 */
export const openaiModelsApi = {
  /**
   * Get all OpenAI models
   */
  async getAll(activeOnly: boolean = true): Promise<OpenAIModel[]> {
    return handleApiRequest<OpenAIModel[]>(
      `/api/openai-models/?active_only=${activeOnly}`
    );
  },

  /**
   * Get default OpenAI model
   */
  async getDefault(): Promise<OpenAIModel> {
    return handleApiRequest<OpenAIModel>(
      '/api/openai-models/default'
    );
  },

  /**
   * Set default OpenAI model
   */
  async setDefault(modelId: string): Promise<OpenAIModel> {
    return handleApiRequest<OpenAIModel>(
      `/api/openai-models/default/${modelId}`,
      { method: 'POST' }
    );
  },

  /**
   * Create a new OpenAI model
   */
  async create(model: Partial<OpenAIModel>): Promise<OpenAIModel> {
    return handleApiRequest<OpenAIModel>(
      '/api/openai-models/',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(model)
      }
    );
  },

  /**
   * Update an OpenAI model
   */
  async update(modelId: string, model: Partial<OpenAIModel>): Promise<OpenAIModel> {
    return handleApiRequest<OpenAIModel>(
      `/api/openai-models/${modelId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(model)
      }
    );
  },

  /**
   * Delete an OpenAI model
   */
  async delete(modelId: string): Promise<{ message: string }> {
    return handleApiRequest<{ message: string }>(
      `/api/openai-models/${modelId}`,
      { method: 'DELETE' }
    );
  }
};

/**
 * Analysis tools API functions
 */
export const analysisToolsApi = {
  /**
   * Check stationarity of a time series
   */
  async checkStationarity(timeSeriesId: number | string): Promise<{
    is_stationary_adf: boolean;
    is_stationary_kpss: boolean;
    consensus_stationary: boolean;
    adf_test: {
      p_value: number;
      test_statistic: number;
      critical_values: Record<string, number>;
    };
    kpss_test: {
      p_value: number;
      test_statistic: number;
      critical_values: Record<string, number>;
    };
  }> {
    return handleApiRequest<{
      is_stationary_adf: boolean;
      is_stationary_kpss: boolean;
      consensus_stationary: boolean;
      adf_test: {
        p_value: number;
        test_statistic: number;
        critical_values: Record<string, number>;
      };
      kpss_test: {
        p_value: number;
        test_statistic: number;
        critical_values: Record<string, number>;
      };
    }>(
      `/api/analysis/stationarity/${timeSeriesId}`
    );
  }
};

/**
 * User API functions
 */
export const userApi = {
  /**
   * Get all users
   */
  async getAll(skip: number = 0, limit: number = 100): Promise<{users: any[], total: number}> {
    return handleApiRequest<{users: any[], total: number}>(
      `/api/users/?skip=${skip}&limit=${limit}`
    );
  },

  /**
   * Get a user by ID
   */
  async getById(userId: number): Promise<any> {
    return handleApiRequest<any>(
      `/api/users/${userId}`
    );
  },

  /**
   * Create a new user
   */
  async create(user: any): Promise<any> {
    return handleApiRequest<any>(
      '/api/users/',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(user)
      }
    );
  },

  /**
   * Update a user
   */
  async update(userId: number, user: any): Promise<any> {
    return handleApiRequest<any>(
      `/api/users/${userId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(user)
      }
    );
  },

  /**
   * Delete a user
   */
  async delete(userId: number): Promise<{ message: string }> {
    return handleApiRequest<{ message: string }>(
      `/api/users/${userId}`,
      { method: 'DELETE' }
    );
  }
};

/**
 * AI Prompts API functions
 */
export const aiPromptsApi = {
  /**
   * Get all AI prompts
   */
  async getAll(activeOnly: boolean = false): Promise<any[]> {
    return handleApiRequest<any[]>(
      `/api/ai-prompts/?active_only=${activeOnly}`
    );
  },

  /**
   * Get an AI prompt by ID
   */
  async getById(promptId: number): Promise<any> {
    return handleApiRequest<any>(
      `/api/ai-prompts/${promptId}`
    );
  },

  /**
   * Get an AI prompt by purpose
   */
  async getByPurpose(purpose: string): Promise<any> {
    return handleApiRequest<any>(
      `/api/ai-prompts/purpose/${purpose}`
    );
  },

  /**
   * Create a new AI prompt
   */
  async create(prompt: any): Promise<any> {
    return handleApiRequest<any>(
      '/api/ai-prompts/',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(prompt)
      }
    );
  },

  /**
   * Update an AI prompt
   */
  async update(promptId: number, prompt: any): Promise<any> {
    return handleApiRequest<any>(
      `/api/ai-prompts/${promptId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(prompt)
      }
    );
  },

  /**
   * Delete an AI prompt
   */
  async delete(promptId: number): Promise<{ message: string }> {
    return handleApiRequest<{ message: string }>(
      `/api/ai-prompts/${promptId}`,
      { method: 'DELETE' }
    );
  }
};

/**
 * System API functions
 */
export const systemApi = {
  /**
   * Check API health
   */
  async healthCheck(): Promise<{ status: string }> {
    return handleApiRequest<{ status: string }>(
      '/api/health'
    );
  }
};

/**
 * Convenient API functions for the admin panel
 */

/**
 * List all users
 */
export async function listUsers(skip: number = 0, limit: number = 100): Promise<import('./types').User[]> {
  const response = await userApi.getAll(skip, limit);
  return response?.users || [];
}

/**
 * List all time series
 */
export async function listTimeSeries(skip: number = 0, limit: number = 100): Promise<TimeSeriesMeta[]> {
  const response = await timeSeriesApi.getAll(skip, limit);
  return response?.data || [];
}

/**
 * List all OpenAI models
 */
export async function listOpenAIModels(activeOnly: boolean = false): Promise<OpenAIModel[]> {
  return await openaiModelsApi.getAll(activeOnly);
}

/**
 * List all AI prompts
 */
export async function listAIPrompts(activeOnly: boolean = false): Promise<any[]> {
  return await aiPromptsApi.getAll(activeOnly);
}